import type { AnalysisDaySchema } from '@libs/data-access/entities'
import { analysisService } from '@libs/data-access/queries'
import { AlertErrorAdapter, Flex, Loader, Text, Title } from '@libs/ui/ds'
import { usePlanningAnalysisTranslation } from '@libs/utils/translations'
import { useMemoSelector } from '@spa/redux/hooks'
import { getParams } from '@spa/scenes/Plannings/redux/selectors'
import { canEditEmployees } from '@spa/services/accessRules'
import React from 'react'
import type { FC } from 'react'
import { getCurrentMembershipSelector } from 'snap-redux/modules/memberships/selectors'

import DailyStatistics from './components/DailyStatistics/DailyStatistics'
import WeeklySummary from './components/WeeklySummary/WeeklySummary'

type Props = {
  real: boolean
  toggleReal: () => void
}
const StatisticsComponent: FC<Props> = ({ real, toggleReal }) => {
  const { t } = usePlanningAnalysisTranslation()
  const { locationId, date, teamId } = useMemoSelector(getParams)
  const currentMembership = useMemoSelector(getCurrentMembershipSelector)

  const canSeeWagesRatio = canEditEmployees(currentMembership)

  const query = analysisService.useFetch({
    queryParams: {
      team_id: teamId && locationId ? teamId.toString() : undefined,
      location_id: !teamId && locationId ? locationId.toString() : undefined,
      date
    }
  })

  if (query.isLoading) {
    return <Loader.Container className='h-screen' />
  }

  const dailyStatistics: AnalysisDaySchema[] | undefined = teamId
    ? query.data?.team?.days
    : query.data?.location?.days

  if (!dailyStatistics || !query.data || !query.data.total) {
    return (
      <div className='m-3'>
        <AlertErrorAdapter error={{ message: t('statistics.error') }} />
      </div>
    )
  }

  return (
    <Flex row className='relative border-b-1 border-b-neutral-200'>
      <WeeklySummary
        canSeeWagesRatio={canSeeWagesRatio}
        real={real}
        toggleReal={toggleReal}
        statistics={query.data.total}
      />
      <Flex row className='mb-2.5 ml-2.5 flex-nowrap items-center gap-1'>
        <Title size={5}>{t('statistics.weeklyProductivityIndicative')}</Title>
        <Text as='span' size='small' className='italic'>
          {t('statistics.weeklyProductivityIndicativeDescription')}
        </Text>
      </Flex>
      <DailyStatistics
        real={real}
        canSeeWagesRatio={canSeeWagesRatio}
        dailyStatistics={dailyStatistics}
        teamId={teamId}
        locationId={locationId}
      />
    </Flex>
  )
}
export const Statistics = React.memo(StatisticsComponent)
